// ChatBox.tsx
import React, { useState, useRef, useEffect } from "react";

interface ChatBoxProps {
  onSendMessage: (message: string, model: any) => Promise<any>; // Update return type to Promise<string>
  generativeModel: any;
  generatedResponse: any;
}

interface ChatMessage {
  role: "user" | "ai";
  content: string;
  timestamp: string;
}
// ChatBox.tsx
// ... (other imports and interfaces)
// ... (other imports and interfaces)

const ChatBox: React.FC<ChatBoxProps> = ({
  onSendMessage,
  generativeModel,
  generatedResponse,
}) => {
  const [userMessage, setUserMessage] = useState<string>("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const inputRef = useRef<HTMLDivElement>(null);

  const handleSendMessage = async (message: string) => {
    if (message && generativeModel) {
      const timestamp = new Date().toLocaleTimeString();

      // Add user message to chat
      setChatMessages((prevMessages) => [
        ...prevMessages,
        { role: "user", content: message, timestamp },
      ]);

      // Call the onSendMessage prop
      await onSendMessage(message, generativeModel);

      setUserMessage("");
    }
  };

  useEffect(() => {
    // Scroll to the bottom when chat messages change
    if (inputRef.current) {
      inputRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  useEffect(() => {
    // Handle AI response when generatedResponse changes
    if (generatedResponse) {
      const aiTimestamp = new Date().toLocaleTimeString();

      // Check if the last message is from AI
      if (
        chatMessages.length > 0 &&
        chatMessages[chatMessages.length - 1].role === "ai"
      ) {
        // Update the content of the last AI message
        setChatMessages((prevMessages) => [
          ...prevMessages.slice(0, -1), // Remove the last AI message
          {
            role: "ai",
            content: generatedResponse,
            timestamp: aiTimestamp,
          },
        ]);
        setUserMessage("");
      } else {
        // Add AI response to chat if the last message is not from AI
        setChatMessages((prevMessages) => [
          ...prevMessages,
          { role: "ai", content: generatedResponse, timestamp: aiTimestamp },
        ]);
        setUserMessage("");
      }
    }
  }, [generatedResponse]);

  useEffect(() => {
    // Add logic to handle initial AI response when the component mounts
    handleSendMessage(
      "Give me a short text asking me about my day and orienting me towards self-reflection subtly as if you are my personal AI diary. Professional tone and less than 4 sentences. Start with Hello there!, Hey there! or Hi there!"
    );
  }, []); // Empty dependency array to ensure it runs only once

  return (
    <div className="  flex flex-col   bg-slate-900 max-h-96 ">
      <div className=" bg-slate-600 overflow-y-auto  ">
        {/* Display Chat Messages */}
        {chatMessages.map((msg, index) => (
          <div
            key={index}
            className={msg.role === "user" ? "text-white" : "text-green-500"}
          >
            <strong
              className={
                msg.role === "user" ? "text-slate-200 " : "text-green-400"
              }
            >
              {msg.role === "user" ? "You: " : "AI: "}
            </strong>
            {msg.content} <span className="text-gray-400">{msg.timestamp}</span>
          </div>
        ))}
        <div ref={inputRef}></div>
      </div>

      {/* User Input */}
      <div className="rounded-full py-2 flex justify-end text-wrap">
        <textarea
          value={userMessage}
          onChange={(e) => setUserMessage(e.target.value)}
          placeholder="Type your message..."
          className="flex-grow p-2 rounded-l-md border w-4/5 border-gray-300 min-h-36 md:min-h-24 h-full max-h-80"
        />

        <button
          onClick={() => handleSendMessage(userMessage)}
          className="bg-blue-800/75 text-white p-2 rounded-r-md"
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default ChatBox;
